import { ApiResponse, BfiApi, IApiResponse, SearchResponse } from './bfiApi';
import { ISearchCriteria } from './common';
import { GridSettingType } from '../../../constants/src';
import { SimpleCache } from '../../../components/src';
import { UserAgreementType } from '../../../constants/src';

export interface IUserLean {
    id: number;
    email: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    phone: string;
}

export interface IUserSearchResponse extends Omit<IApiResponse, 'result'> {
    result: [IUserLean];
}

export interface IGeneralContractorSummary {
    id: number;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    created: Date;
    lastActive?: Date;
    lastPasswordResetRequested?: Date;
    lastPasswordResetSent?: Date;
    lastFailedLoginAttempt?: Date;
    lastLoginIPAddress: string;
    eSignDate?: Date;
    userRole?: string;
}

export interface IGeneralContractor {
    id: number;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    properties: IGeneralContractorProperty[];
}

export interface IGeneralContractorProperty {
    id: number;
    bfiAuthorized: boolean;
    fullAddress: string;
    bfiNumber: string;
    borrowerEntity: string;
}

export interface IPrincipalUser {
    id: string;
    principalId: number;
    firstName: string;
    lastName: string;
    email: string;
    created: Date;
    lastActive?: Date;
    lastPasswordResetRequested?: Date;
    lastPasswordResetSent?: Date;
    lastFailedLoginAttempt?: Date;
    lastLoginIPAddress: string;
    eSignDate?: Date;
    isLoc: boolean;
    submittedAppCount: number;
    unSubmittedAppCount: number;
}

export interface IPrincipalUsersResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IPrincipalUser];
    };
}

export interface IBrokerUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    created: Date;
    lastActive?: Date;
    lastPasswordResetRequested?: Date;
    lastPasswordResetSent?: Date;
    lastFailedLoginAttempt?: Date;
    lastLoginIPAddress: string;
    iccaType: string;
    amountClosed: number;
    brokerId: number;
}

export interface IUserAgreement {
    id: number;
    userId: string;
    userAgreementTypeId: number;
    userAgreementType: string;
    dateSigned?: Date;
    ipAddress: string;
    templateVersionId: number;
    templateVersion: ITemplateVersionInfo;
    fileLink: string;
    signedUserAgreementGeneratingUrl: string;
    SignedUserAgreementDownloadToken: string;
}

export interface ITemplateVersionInfo {
    id: number;
    name: string;
}

export interface IGetBrokerUsersResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IBrokerUser];
    };
}

export interface IGetUserAgreementsResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IUserAgreement];
    };
}

export interface IUserGridSetting {
    storageKey: string;
    gridTypeId: GridSettingType;
}

export interface ISignUserAgreement {
    userId: number;
    fontId: number;
    userAgreementTypeId: UserAgreementType;
    ipAddress: string;
}

export interface IUserGridSettingResponse extends Omit<IApiResponse, 'result'> {
    result: IUserGridSetting;
}

export interface IPrincipalUsersSearchCriteria extends ISearchCriteria {
    filter: number;
}

export interface IBrokerUsersSearchCriteria extends ISearchCriteria {
    filter: number;
}

const gridCache = new SimpleCache();

export class UsersApi extends BfiApi {
    getHighlights(): Promise<IApiResponse> {
        return super.get('/user/highlights');
    }

    async getPrincipalUsers(criteria: IPrincipalUsersSearchCriteria): Promise<IPrincipalUsersResponse> {
        return super.post('/user/grids/principalUsers', criteria);
    }

    async getGeneralContractorUsers(searchText?: string): Promise<SearchResponse<IGeneralContractorSummary[]>> {
        return super.post('/user/grids/generalContractorUsers', { searchText: searchText ?? '' });
    }

    async getGeneralContractorUser(id: number): Promise<ApiResponse<IGeneralContractor>> {
        return super.get(`/user/generalContractor/${id}`);
    }

    async saveGeneralContractor(contactor: IGeneralContractor): Promise<ApiResponse<IGeneralContractor>> {
        return super.post('/user/saveGeneralContractor', contactor);
    }

    async getBrokerUsers(criteria: IBrokerUsersSearchCriteria): Promise<IGetBrokerUsersResponse> {
        return super.post('/user/grids/brokerUsers', criteria);
    }

    async getUserAgreements(id: number): Promise<IGetUserAgreementsResponse> {
        return super.post('/account/grids/agreements', id);
    }

    async createUserAgreementPdf(id: number): Promise<IApiResponse> {
        return super.post(`/account/${id}/createUserAgreementPdf`);
    }

    async signUserAgreement(agreement: ISignUserAgreement): Promise<IApiResponse> {
        return super.post('/account/signuseragreement', agreement);
    }

    async loadUserGridSetting(gridTypeId: GridSettingType): Promise<string> {
        const key = gridTypeId.toString();
        const data = gridCache.get(key);
        if (!data) {
            const retVal = new Promise<string>((resolve) => {
                super.get<IUserGridSettingResponse>(`/account/user/gridsetting/load/${gridTypeId}`).then((response) => {
                    if (!response.result?.storageKey) {
                        resolve('');
                    } else {
                        resolve(response.result.storageKey);
                    }
                });
            });
            gridCache.set(key, retVal);
            return retVal;
        }
        return data;
    }

    async saveUserGridSetting(gridSettings: IUserGridSetting): Promise<IUserGridSettingResponse> {
        gridCache.remove(gridSettings.gridTypeId.toString());
        return super.post('/account/user/gridsetting/save', gridSettings);
    }
}
